import { makeAutoObservable } from "mobx"
import { DropzoneOptions } from "react-dropzone"
import Loader from "reusable/mobx-helpers/Loader"
import { Deferred, notNull } from "@gap/utils/utils"
import WebSocketStore from "websockets/WebSocketStore"
import { orgContextCache } from "cache/orgContextCache"
import { appendUlidToFile, fileIsAGapImage, formatFile } from "reusable/utils/file-operations"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { Timeouts } from "@gap/utils/date-utils"

export class FileUploadStore {
   sp: Omit<FileUploadStoreProps, 'files'>
   files: File[]
   validationText = ''
   readonly loader = new Loader()
   /** A map of active uploads, keyed by their file names */
   private uploadMap: Record<string, ActiveUpload> = {}

   constructor({ files, ...sp }: FileUploadStoreProps) {
      this.sp = sp
      this.setFiles(files)
      makeAutoObservable(this)
   }

   get authOrgId() {
      return notNull(orgContextCache.authOrgId.get())
   }

   get activeUploads() {
      return Object.values(this.uploadMap)
   }

   /**
    * The overall progress on all current uploads as a number out of 100
    */
   get progress() {
      const sum = this.activeUploads.reduce<number>((running, { uploadProgressPercent }) => running + uploadProgressPercent, 0)
      return this.activeUploads.length === 0 ? 0 : Math.round(sum / this.activeUploads.length)
   }

   get validFiles() {
      return Object.values(this.uploadMap)
         .reduce<File[]>(
            (files, { status, file }) => [...files, ...(status === UploadStatus.valid ? [file] : [])],
            [],
         );
   }

   setFiles = (files: File[]) => {
      this.files = files
      this.uploadMap = {}
      this.files.forEach(async file => {
         this.uploadMap[file.name] = {
            file,
            status: UploadStatus.valid,
            uploadProgressPercent: 100,
            cancel: () => this.cancelAndRemove({ filename: file.name }),
         }
         this.uploadMap[file.name].imgUrl = ""
         const imgUrl = fileIsAGapImage(file) && this.sp.getUploadedFileUrl ? await this.sp.getUploadedFileUrl(file) : undefined
         this.uploadMap[file.name].imgUrl = imgUrl
      })
   }

   onDropAccepted: DropzoneOptions['onDropAccepted'] = async (files) => {
      const newFiles: File[] = this.sp.appendUlids
         ? files.map((file) => appendUlidToFile(file))
         : files
      this.files = [...new Set([...this.files, ...newFiles])]
      await this.uploadFiles(newFiles)
      if (this.validFiles.length) {
         this.sp.onFilesValidated?.(this.validFiles)
      }
   }

   onDropRejected: DropzoneOptions['onDropRejected'] = async (rejections) => {
      const errorCodes = rejections.flatMap(({ errors }) => errors.flatMap(({ code }) => code))
      if (errorCodes.includes('file-too-large')) {
         this.validationText = 'File is too large'
      }
      else if (errorCodes.includes('file-invalid-type')) {
         this.validationText = 'File has unsupported type/extension'
      }
      else {
         this.validationText = 'File is invalid'
      }
      setTimeout(() => this.validationText = '', Timeouts.second * 5)
   }

   cancelAndRemove = async ({ filename }: { filename: string }) => {
      this.uploadMap[filename].xhr?.abort()
      await this.unsubscribeFromValidation({ filename })
      clearTimeout(this.uploadMap[filename].validationTimer)
      this.uploadMap[filename].deferredValidation?.resolve()
      this.uploadMap[filename].imgUrl = undefined
      delete this.uploadMap[filename]
      const valueIndex = this.files.findIndex((file) => file.name === filename)
      this.files = this.files.filter((_, index) => index !== valueIndex)
      this.sp.onFileRemove?.(filename)
      if (this.validFiles.length) {
         this.sp.onFilesValidated?.(this.validFiles)
      } else {
         this.sp.onLastFileRemoved?.()
      }
   }

   unsubscribeFromValidation = ({ filename }: { filename: string }) => this.sp.websocketStore.removeSubscriptions({
      orgId: this.sp.fileOrgId,
      subscriptions: [{
         event: 'upload-status-changed',
         entityId: filename,
      }],
      userId: this.sp.authUserId,
   })

   uploadFiles = async (files: File[]) => {
      // For single file uploads, clear everything first
      if (!this.sp.maxFiles || this.sp.maxFiles === 1) {
         // Cancel any existing uploads
         Object.keys(this.uploadMap).forEach(filename => {
            this.uploadMap[filename].xhr?.abort()
            this.unsubscribeFromValidation({ filename })
         })
         this.files = []
         this.uploadMap = {}
      }

      this.sp.onUploadStarted?.()

      await this.loader.executeLoading(() => Promise.all(
         files.map(async (unsafeFile) => {
            const file = this.sp.formatFile ? formatFile(unsafeFile, { appendUlid: true }) : unsafeFile
            this.uploadMap[file.name] = {
               file,
               status: UploadStatus.uploading,
               uploadProgressPercent: 0,
               cancel: () => this.cancelAndRemove({ filename: file.name }),
               imgUrl: fileIsAGapImage(file) && this.sp.getUploadedFileUrl ? "" : undefined
            }
            try {
               const { deferredValidation, waitForValidation } = await this.initValidation({ file })
               if (!this.uploadMap[file.name]) return;
               this.uploadMap[file.name].deferredValidation = deferredValidation
               const uploadUrl = await this.sp.getPresignedUrl(file)
               if (!this.uploadMap[file.name]) return;
               this.uploadMap[file.name].uploadUrl = uploadUrl
               await this.putFile({ file, uploadUrl })
               if (!this.uploadMap[file.name]) return;
               this.setFileStatus(file, UploadStatus.validating)
               try {
                  const { rejectTimer } = await waitForValidation({ timeout: Timeouts.second * 20 })
                  if (!this.uploadMap[file.name]) return;
                  this.uploadMap[file.name].validationTimer = rejectTimer
                  this.uploadMap[file.name].status = UploadStatus.valid
                  // If the file is an image, retrieve image url
                  if (fileIsAGapImage(file) && this.sp.getUploadedFileUrl) {
                     this.uploadMap[file.name].imgUrl = await this.sp.getUploadedFileUrl(file)
                  }
               } catch {
                  this.setFileStatus(file, UploadStatus.invalid)
               }
            } catch {
               this.setFileStatus(file, UploadStatus.failed)
            }
         })
      ))
      // Remove subscriptions without awaiting as this shouldn't block users continuing
      files.map((file) => this.unsubscribeFromValidation({ filename: file.name }))
   }

   setFileStatus = (file: File, status: UploadStatus) => {
      if (this.uploadMap[file.name]) {
         this.uploadMap[file.name].status = status
      }
   }

   putFile = async ({ file, uploadUrl }: PutFileParams) => {
      const xhr = new XMLHttpRequest()
      this.uploadMap[file.name].xhr = xhr
      return new Promise<void>((resolve, reject) => {
         xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE) {
               if (xhr.status === 200) {
                  resolve()
               }
               else if (xhr.status === 0) {
                  reject(new Error('Upload cancelled', { cause: 'cancelled' }))
               }
               else {
                  reject(new Error(`Upload failed with status code ${xhr.status}`, { cause: xhr.status }))
               }
            }
         }

         xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
               const percentComplete = Math.round((event.loaded / file.size) * 100)
               this.uploadMap[file.name].uploadProgressPercent = percentComplete
            }
         }

         xhr.open('PUT', uploadUrl)
         if (file.type) {
            xhr.setRequestHeader('Content-Type', file.type)
         }
         xhr.send(file)
      })
   }

   initValidation = async ({ file }: { file: File }) => {
      let rejectTimer: NodeJS.Timeout

      const deferredValidation = new Deferred()

      await this.sp.websocketStore.addSubscriptions({
         orgId: this.authOrgId,
         userId: this.sp.authUserId,
         subscriptions: [{
            event: 'upload-status-changed',
            entityId: file.name,
            callback: async (response) => {
               clearTimeout(rejectTimer)
               if (response.message === 'passed') {
                  deferredValidation.resolve()
               } else {
                  deferredValidation.reject(new Error(`Failed to validate ${file.name}`))
               }
            },
         }]
      })

      const waitForValidation = async ({ timeout }: WaitForValidationParams) => {
         rejectTimer = setTimeout(() => deferredValidation.reject(new Error(`Validation timed out for ${file.name}`)), timeout)
         await deferredValidation.promise
         return { rejectTimer }
      }

      return {
         deferredValidation,
         waitForValidation,
      }
   }
}

export const { Provider: FileUploadStoreStoreProvider, useProvidedStore: useFileUploadStoreStore } = createStoreContext(FileUploadStore)

interface FileUploadStoreProps {
   files: File[],
   fileOrgId: string,
   appendUlids?: boolean,
   getPresignedUrl: (file: File) => Promise<string>
   onFilesValidated?: (files: File[]) => any
   onLastFileRemoved?: () => void
   onFileRemove?: (fileName: string) => void
   onUploadStarted?: () => void
   websocketStore: WebSocketStore,
   maxFiles?: number,
   getUploadedFileUrl?: (file: File) => Promise<string>
   formatFile?: boolean
   authUserId: string
}

interface PutFileParams {
   file: File,
   uploadUrl: string,
}

interface WaitForValidationParams {
   timeout: number,
}

export interface ActiveUpload {
   status: UploadStatus,
   file: File,
   cancel: () => Promise<any>,
   uploadProgressPercent: number,
   uploadUrl?: string,
   xhr?: XMLHttpRequest,
   deferredValidation?: Deferred,
   validationTimer?: NodeJS.Timeout,
   /** Url of the image. If loading, it is an empty string. If not used, it is undefined */
   imgUrl?: string
}

export enum UploadStatus {
   uploading = 'uploading...',
   validating = 'finishing up...',
   valid = 'done',
   invalid = 'file is invalid',
   failed = 'upload failed',
}
